const BASE = '/api';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await res.json();
  if (!res.ok) throw new Error((data as any).error || 'Request failed');
  return data as T;
}

export interface User {
  id: string;
  email: string;
  full_name: string | null;
  mobile: string | null;
  address: string | null;
  google_id: string | null;
  is_admin: number;
  is_active: number;
  email_verified: number;
  notification_preference: string;
  terms_accepted_at: number | null;
  marketing_consent_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface Reminder {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  due_date: string;
  reminder_offset: 'day_before' | 'week_before' | 'month_before';
  reminder_date: string;
  is_completed: number;
  is_deleted: number;
  notification_sent: number;
  created_at: number;
  updated_at: number;
}

// Auth
export const auth = {
  register: (email: string, password: string, termsAccepted: boolean, fullName?: string, marketingConsent?: boolean) =>
    request<{ message: string }>('POST', '/auth/register', { email, password, fullName, termsAccepted, marketingConsent }),

  login: (email: string, password: string) =>
    request<{ user: User }>('POST', '/auth/login', { email, password }),

  logout: () => request<{ message: string }>('POST', '/auth/logout'),

  forgotPassword: (email: string) =>
    request<{ message: string }>('POST', '/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    request<{ message: string }>('POST', '/auth/reset-password', { token, password }),

  me: () => request<{ user: User }>('GET', '/auth/me'),

  googleUrl: (termsAccepted?: boolean, marketingConsent?: boolean) => {
    const params = new URLSearchParams();
    if (termsAccepted) params.set('terms', '1');
    if (marketingConsent) params.set('marketing', '1');
    const qs = params.toString();
    return `/api/auth/google${qs ? `?${qs}` : ''}`;
  },
};

// Profile
export const profile = {
  update: (data: Partial<User> & { marketingConsent?: boolean }) => request<{ user: User }>('PATCH', '/profile', data),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string }>('PATCH', '/profile/password', { currentPassword, newPassword }),
};

// Reminders
export const reminders = {
  list: () => request<{ reminders: Reminder[] }>('GET', '/reminders'),
  create: (data: { title: string; description?: string; due_date: string; reminder_offset: string }) =>
    request<{ reminder: Reminder }>('POST', '/reminders', data),
  update: (id: string, data: Partial<Reminder>) =>
    request<{ reminder: Reminder }>('PUT', `/reminders/${id}`, data),
  remove: (id: string) => request<{ message: string }>('DELETE', `/reminders/${id}`),
  toggleComplete: (id: string) => request<{ message: string }>('PATCH', `/reminders/${id}/complete`),
};

export interface VisitStats {
  total: number;
  byDevice: Record<string, number>;
  lastVisit: number | null;
}

export interface StatsPoint {
  period: string;
  impressions: number;
  unique_visitors: number;
  new_visitors: number;
  returning_visitors: number;
}

export type StatsRange = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface VisitDetail {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_full_name: string | null;
  session_id: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
  path: string;
  ip_address: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  timezone: string | null;
  browser: string | null;
  os: string | null;
  referrer: string | null;
  is_returning: number;
  created_at: number;
}

// Admin
export const admin = {
  users: (search?: string, page?: number) =>
    request<{ users: User[]; total: number; page: number; limit: number }>(
      'GET', `/admin/users?${new URLSearchParams({ search: search || '', page: String(page || 1) })}`
    ),
  getUser: (id: string) =>
    request<{ user: User; reminders: Reminder[]; visitStats: VisitStats }>('GET', `/admin/users/${id}`),
  createUser: (data: { email: string; password: string; full_name?: string; is_admin?: boolean }) =>
    request<{ user: User }>('POST', '/admin/users', data),
  updateUser: (id: string, data: Partial<User>) =>
    request<{ user: User }>('PUT', `/admin/users/${id}`, data),
  deleteUser: (id: string) => request<{ message: string }>('DELETE', `/admin/users/${id}`),
  resetUserPassword: (id: string, password: string) =>
    request<{ message: string }>('PATCH', `/admin/users/${id}/password`, { password }),
  stats: (range: StatsRange) =>
    request<{
      totalUsers: number; totalImpressions: number; newVisitors: number; returningVisitors: number;
      range: StatsRange; data: StatsPoint[];
    }>('GET', `/admin/stats?range=${range}`),
  visits: (page?: number) =>
    request<{ visits: VisitDetail[]; total: number; page: number; limit: number }>(
      'GET', `/admin/visits?page=${page || 1}`
    ),
};
